import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { DatePicker } from "@/components/DatePicker";
import { SERVICE } from "@/lib/format";


const EMPTY = { service_type: "DELIVERY", external_order_id: "", project_id: "", technology: "", priority: "ORDINARIA", customer_name: "", phone: "", city: "", province: "", region: "", street: "", number: "", staircase: "", floor: "", apartment: "", notes: "", appointment_date: "", activation_type: "", network_code: "", roe: "", port_splitter: "", pto: "", pop: "", resource_id: "", building_id: "", equipment: "", time_flex: "", appointment_time: "", symptom: "", opened_at: "" };

const fromOrder = (o) => ({ ...EMPTY, ...o.address, ...(o.delivery || {}), symptom: o.assurance?.symptom || "", service_type: o.service_type, external_order_id: o.external_order_id, project_id: o.project_id, technology: o.technology, priority: o.priority, customer_name: o.customer_name, phone: o.phone, notes: o.notes, appointment_date: o.appointment_date || "" });

const F = ({ label, k, form, set, className = "" }) => (
  <div className={className}>
    <Label className="micro-label">{label}</Label>
    <Input className="mt-1.5" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`order-form-${k}`} />
  </div>
);

const Pick = ({ label, k, form, set, options, disabled }) => (
  <div>
    <Label className="micro-label">{label}</Label>
    <Select value={form[k] || undefined} onValueChange={(v) => set(k, v)} disabled={disabled}>
      <SelectTrigger className="mt-1.5" data-testid={`order-form-${k}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const OrderFormDialog = ({ open, onOpenChange, order, onSaved }) => {
  const { projects } = useData();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) setForm(order ? fromOrder(order) : EMPTY);
  }, [open, order]);

  const project = projects.find((x) => x.id === form.project_id);
  const onProject = (id) => {
    const p = projects.find((x) => x.id === id);
    setForm((f) => ({ ...f, project_id: id, service_type: p?.service_types[0] || f.service_type, region: f.region || p?.regions[0] || "" }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = order ? await api.put(`/orders/${order.id}`, form) : await api.post("/orders", form);
      toast.success(order ? "Ordine aggiornato" : `Ordine ${data.external_order_id} creato (stato Ricevuto)`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const p = { form, set };
  const isDel = form.service_type !== "ASSURANCE";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="order-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{order ? `Modifica ${order.external_order_id}` : "Nuovo ordine / ticket"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Pick label="Commessa" k="project_id" form={form} set={(k, v) => onProject(v)} disabled={!!order} options={projects.map((x) => ({ value: x.id, label: `${x.code} — ${x.name}` }))} />
          </div>
          <Pick label="Attività" k="service_type" {...p} disabled={!!order} options={(project?.service_types || ["DELIVERY", "ASSURANCE", "CONSTRUCTION"]).map((s) => ({ value: s, label: SERVICE[s] }))} />
          <div>
            <Label className="micro-label">{isDel ? "Codice ordine committente" : "Ticket guasto"}</Label>
            <Input className="mt-1.5 font-mono" value={form.external_order_id} disabled={!!order} onChange={(e) => set("external_order_id", e.target.value)} data-testid="order-form-external_order_id" />
          </div>
          <Pick label="Tecnologia" k="technology" {...p} options={["FTTH", "FTTC", "FTTB", "Rame"]} />
          <Pick label="Priorità" k="priority" {...p} options={[{ value: "ORDINARIA", label: "Ordinaria" }, { value: "URGENTE", label: "Urgente" }, { value: "CRITICA", label: "Critica" }]} />
          <F label="Cliente finale" k="customer_name" {...p} className="sm:col-span-2" />
          <F label="Telefono" k="phone" {...p} />
          <F label="Indirizzo" k="street" {...p} className="sm:col-span-2" />
          <F label="Civico" k="number" {...p} />
          <F label="Località" k="city" {...p} />
          <F label="Provincia" k="province" {...p} />
          <F label="Regione" k="region" {...p} />
          <F label="Scala" k="staircase" {...p} />
          <F label="Piano" k="floor" {...p} />
          <F label="Interno" k="apartment" {...p} />
          {isDel ? (
            <>
              <Pick label="Tipo attivazione" k="activation_type" {...p} options={["Nuova linea", "Migrazione", "Variazione", "Trasloco"]} />
              <F label="Codice ordine OLO" k="network_code" {...p} />
              <F label="ROE / PTE" k="roe" {...p} />
              <F label="PFS / Armadio" k="port_splitter" {...p} />
              <F label="PTO" k="pto" {...p} />
              <F label="POP" k="pop" {...p} />
              <F label="ID Risorsa" k="resource_id" {...p} />
              <F label="ID Building" k="building_id" {...p} />
              <F label="Materiale / Psw apparato" k="equipment" {...p} />
              <F label="Anticipo / Posticipo" k="time_flex" {...p} />
              <F label="Ora appuntamento" k="appointment_time" {...p} />
              <div>
                <Label className="micro-label">Data appuntamento</Label>
                <div className="mt-1.5"><DatePicker value={form.appointment_date} onChange={(v) => set("appointment_date", v)} testId="order-form-appointment" /></div>
              </div>
            </>
          ) : (
            <F label="Sintomo" k="symptom" {...p} className="sm:col-span-3" />
          )}
          <div className="sm:col-span-3">
            <Label className="micro-label">Note</Label>
            <Textarea className="mt-1.5" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="order-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy || !form.project_id || !form.external_order_id} data-testid="order-form-submit">{busy ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
